import { isDefined } from "../../infra3d";
import { StyleUtils } from "../../infra3d";
import { Obj } from "../object";
/**
 * Style definitions for Feature rendering
 *
 * @class FeatureStyle
 */
export class FeatureStyle extends Obj {
    constructor(styledefinition) {
        super();
        this.stylename = "default";
        this.rules = [
            {
                filter: "",
                symbolizer: "point",
                params: {
                    size: 4,
                    stroke: [1, 0, 0, 1],
                },
            },
        ];
        if (isDefined(styledefinition) && styledefinition != null) {
            this.stylename = styledefinition["stylename"];
            this.rules = styledefinition["rules"];
        }
    }
    /**
     * export current style to JSON
     * @param {Object} styledefinition
     */
    fromJSON(styledefinition) {
        this.stylename = styledefinition["stylename"];
        this.rules = styledefinition["rules"];
    }
    /**
     * export current style to the internaly used style
     */
    toMaterialDefinitions(properties) {
        const materialdefinitions = {
            point: null,
            line: null,
            polygon: null,
        };
        for (let i = 0; i < this.rules.length; i++) {
            const rule = this.rules[i];
            if (rule.symbolizer == "point" && this._checkFilter(rule, properties)) {
                const pointStyle = {};
                pointStyle.filter = rule.filter;
                for (const key in this.rules[i].params) {
                    if (!isDefined(pointStyle.stroke)) {
                        pointStyle.stroke = {};
                        pointStyle.stroke.color = "#00FFFF";
                        pointStyle.stroke.alpha = 1;
                        pointStyle.stroke.size = 2;
                    }
                    switch (key) {
                        case "stroke": {
                            const values = this.rules[i].params[key];
                            pointStyle.stroke.color = this._rgbArrayToHex(values);
                            pointStyle.stroke.alpha = values[3];
                            break;
                        }
                        case "size":
                            pointStyle.stroke.size = this.rules[i].params[key];
                            break;
                        case "vertex-colors":
                            pointStyle.stroke.vertexColors = this.rules[i].params[key];
                            break;
                        case "billboard":
                            pointStyle.stroke.billboard = this.rules[i].params[key];
                            break;
                        case "font-marker":
                            pointStyle.stroke.fontMarker = this.rules[i].params[key];
                            break;
                    }
                    pointStyle.stroke.sizeAttenuation = false;
                }
                materialdefinitions[rule.symbolizer] = pointStyle;
            }
            else if (rule.symbolizer == "line" && this._checkFilter(rule, properties)) {
                const lineStyle = {};
                lineStyle.filter = rule.filter;
                for (const key in this.rules[i].params) {
                    if (!isDefined(lineStyle.stroke)) {
                        lineStyle.stroke = {};
                        lineStyle.stroke.color = "#00FFFF";
                        lineStyle.stroke.alpha = 1;
                        lineStyle.stroke.width = 2;
                    }
                    switch (key) {
                        case "stroke": {
                            const values = this.rules[i].params[key];
                            lineStyle.stroke.color = this._rgbArrayToHex(values);
                            lineStyle.stroke.alpha = values[3];
                            break;
                        }
                        case "stroke-width":
                            lineStyle.stroke.width = this.rules[i].params[key];
                            break;
                        case "stroke-sinus-color": {
                            const values = this.rules[i].params[key];
                            lineStyle.stroke.sinusColor = this._rgbArrayToHex(values);
                            if (isDefined(values[3]))
                                lineStyle.stroke.sinusColorAlpha = values[3];
                            break;
                        }
                        case "stroke-dash-width":
                            lineStyle.stroke.dashWidth = this.rules[i].params[key];
                            break;
                        case "stroke-gap-width":
                            lineStyle.stroke.gapWidth = this.rules[i].params[key];
                            break;
                        case "stroke-round-cap":
                            lineStyle.stroke.roundCap = this.rules[i].params[key];
                            break;
                        case "stroke-dash-animation":
                            lineStyle.stroke.animatedDashes = this.rules[i].params[key];
                            break;
                        case "stroke-flash-color": {
                            const values = this.rules[i].params[key];
                            lineStyle.stroke.flashColor = this._rgbArrayToHex(values);
                            if (isDefined(values[3]))
                                lineStyle.stroke.flashColorAlpha = values[3];
                            break;
                        }
                        case "vertex-colors":
                            lineStyle.stroke.vertexColors = this.rules[i].params[key];
                            break;
                    }
                }
                materialdefinitions[rule.symbolizer] = lineStyle;
            }
            else if (rule.symbolizer == "polygon" && this._checkFilter(rule, properties)) {
                const polygonStyle = {};
                polygonStyle.filter = rule.filter;
                for (const key in this.rules[i].params) {
                    if (!isDefined(polygonStyle.fill)) {
                        polygonStyle.fill = {};
                        polygonStyle.fill.color = "#00FFFF";
                        polygonStyle.fill.alpha = 1;
                    }
                    if (!isDefined(polygonStyle.stroke)) {
                        polygonStyle.stroke = {};
                        polygonStyle.stroke.color = "#00FFFF";
                        polygonStyle.stroke.alpha = 1;
                        polygonStyle.stroke.width = 2;
                    }
                    switch (key) {
                        case "fill": {
                            const values = this.rules[i].params[key];
                            polygonStyle.fill.color = this._rgbArrayToHex(values);
                            polygonStyle.fill.alpha = values[3];
                            break;
                        }
                        case "stroke": {
                            const values = this.rules[i].params[key];
                            polygonStyle.stroke.color = this._rgbArrayToHex(values);
                            polygonStyle.stroke.alpha = values[3];
                            break;
                        }
                        case "stroke-width":
                            polygonStyle.stroke.width = this.rules[i].params[key];
                            break;
                        case "vertex-colors":
                            polygonStyle.fill.vertexColors = this.rules[i].params[key];
                            break;
                    }
                }
                materialdefinitions[rule.symbolizer] = polygonStyle;
            }
        }
        if (!materialdefinitions.point)
            materialdefinitions.point = { filter: "" };
        if (!materialdefinitions.line)
            materialdefinitions.line = { filter: "" };
        if (!materialdefinitions.polygon)
            materialdefinitions.polygon = { filter: "" };
        return materialdefinitions;
    }
    /**
     * export raw style using properties
     */
    _checkFilter(rule, featureProperties) {
        if (featureProperties == undefined || featureProperties == null)
            return true;
        const filter = rule["filter"];
        //Removed replacement of white-spaces. due to attributes, where whitespaces are used.
        //the filter must therefore be specified without extra-whitespaces around the logical operators.
        let condition = false;
        if (filter == "") {
            condition = true;
        }
        else {
            if (filter.split("==").length > 1) {
                const split = filter.split("==");
                if (isDefined(featureProperties[split[0]])) {
                    if (split[1] == featureProperties[split[0]])
                        condition = true;
                }
            }
            else if (filter.split(">=").length > 1) {
                const split = filter.split(">=");
                if (isDefined(featureProperties[split[0]])) {
                    if (parseFloat(featureProperties[split[0]]) >= parseFloat(split[1]))
                        condition = true;
                }
            }
            else if (filter.split("<=").length > 1) {
                const split = filter.split("<=");
                if (isDefined(featureProperties[split[0]])) {
                    if (parseFloat(featureProperties[split[0]]) <= parseFloat(split[1]))
                        condition = true;
                }
            }
            else if (filter.split("!=").length > 1) {
                const split = filter.split("!=");
                if (isDefined(featureProperties[split[0]])) {
                    if (split[1] != featureProperties[split[0]])
                        condition = true;
                }
            }
            else if (filter.split(">").length > 1) {
                const split = filter.split(">");
                if (isDefined(featureProperties[split[0]])) {
                    if (parseFloat(featureProperties[split[0]]) > parseFloat(split[1]))
                        condition = true;
                }
            }
            else if (filter.split("<").length > 1) {
                const split = filter.split("<");
                if (isDefined(featureProperties[split[0]])) {
                    if (parseFloat(featureProperties[split[0]]) < parseFloat(split[1]))
                        condition = true;
                }
            }
        }
        return condition;
    }
    /**
     * export current style to JSON
     */
    toJSON() {
        const style = {
            stylename: this.stylename,
            rules: this.rules,
        };
        return JSON.stringify(style);
    }
    _componentToHex(c) {
        const hex = c.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
    }
    _rgbToHex(value) {
        const values = value.split(",");
        return ("#" +
            this._componentToHex(Number(values[0])) +
            this._componentToHex(Number(values[1])) +
            this._componentToHex(Number(values[2])));
    }
    _rgbArrayToHex(values) {
        return ("#" +
            this._componentToHex(Math.floor(values[0] * 255)) +
            this._componentToHex(Math.floor(values[1] * 255)) +
            this._componentToHex(Math.floor(values[2] * 255)));
    }
    rgbaToCSS(value) {
        return ("rgba(" +
            Math.floor(value[0] * 255) +
            "," +
            Math.floor(value[1] * 255) +
            "," +
            Math.floor(value[2] * 255) +
            "," +
            value[3] +
            ")");
    }
    toGeoStyler() {
        return new Promise((resolve, reject) => {
            const geostyle = { name: "", rules: [] };
            geostyle.name = this.stylename;
            const gs_rules = [];
            let all_rules;
            const types = [];
            const filters = [];
            let showonlydefault = false;
            this.rules.forEach((fs_rule) => {
                types.push(fs_rule.symbolizer);
            });
            this.rules
                .reduce((cur, item) => {
                if (types.includes("polygon") && item.symbolizer == "point") {
                    return cur;
                }
                if (types.includes("line") && item.symbolizer == "point") {
                    return cur;
                }
                return cur.then(() => {
                    const gs_rule = { name: "", symbolizers: [], filter: [] };
                    return this._symbolizers2gstyle(item).then((symbolizers) => {
                        if (item.filter == "" && this.rules.length > 1) {
                            if (all_rules) {
                                all_rules = { ...all_rules, ...symbolizers };
                            }
                            else {
                                all_rules = symbolizers;
                            }
                            if (types.includes("polygon") && item.symbolizer != "polygon") {
                                all_rules = FeatureStyle._fsoutline2gsoutline(all_rules);
                            }
                            for (let j = 0; j < gs_rules.length; j++) {
                                gs_rules[j].symbolizers[0] = { ...all_rules, ...gs_rules[j].symbolizers[0] };
                            }
                        }
                        const index = filters.indexOf(item["filter"]);
                        if (index != -1) {
                            if (types.includes("polygon") && item["symbolizer"] != "polygon") {
                                symbolizers = FeatureStyle._fsoutline2gsoutline(symbolizers);
                            }
                            if (gs_rules[index].symbolizers[0].kind == "Fill" && symbolizers.kind == "Line") {
                                gs_rules[index].symbolizers[0] = {
                                    ...symbolizers,
                                    ...gs_rules[index].symbolizers[0],
                                };
                            }
                            else {
                                gs_rules[index].symbolizers[0] = {
                                    ...gs_rules[index].symbolizers[0],
                                    ...symbolizers,
                                };
                            }
                            return new Promise((resolve, reject) => {
                                if (symbolizers) {
                                    resolve(symbolizers);
                                }
                                else {
                                    reject();
                                }
                            });
                        }
                        else {
                            if (item.name || item.filter) {
                                gs_rule.name = item.name || item.filter;
                            }
                            else {
                                if (this.rules.filter((rule) => rule.name == "" || rule.filter == "").length >= 1) {
                                    gs_rule.name = "others";
                                }
                                else {
                                    showonlydefault = true;
                                }
                            }
                            filters.push(item.filter);
                            if (types.includes("polygon") && item.symbolizer != "polygon") {
                                symbolizers = FeatureStyle._fsoutline2gsoutline(symbolizers);
                            }
                            symbolizers = { ...all_rules, ...symbolizers };
                            gs_rule.symbolizers = [symbolizers];
                            // Create Filter
                            if (item.filter) {
                                gs_rule.filter = [item.filter.split(/[A-Za-z1-9]+/)[1]];
                                item.filter.split(/[=><!]+/).forEach((filter) => {
                                    gs_rule.filter.push(filter);
                                });
                            }
                            gs_rules.push(gs_rule);
                            return new Promise((resolve, reject) => {
                                if (symbolizers) {
                                    resolve(symbolizers);
                                }
                                else {
                                    reject();
                                }
                            });
                        }
                    });
                });
            }, Promise.resolve())
                .then(() => {
                if (showonlydefault) {
                    gs_rules.forEach((rule) => {
                        if (!rule.filter) {
                            geostyle.rules = [rule];
                        }
                    });
                    if (geostyle.rules.length == 0) {
                        geostyle.rules = [gs_rules[0]];
                    }
                }
                else {
                    geostyle.rules = gs_rules;
                }
                if (geostyle) {
                    resolve(geostyle);
                }
                else {
                    reject();
                }
            })
                .catch((error) => console.log(error));
        });
    }
    _symbolizers2gstyle(fs_rule) {
        let symbolizers;
        switch (fs_rule.symbolizer) {
            case "polygon":
                symbolizers = { kind: "Fill" };
                if (fs_rule.params.fill) {
                    symbolizers.color = this._rgbArrayToHex(fs_rule.params.fill);
                    symbolizers.fillOpacity = fs_rule.params.fill[3];
                }
                if (fs_rule.params.stroke) {
                    symbolizers.outlineOpacity = fs_rule.params.stroke[3];
                    symbolizers.outlineColor = this._rgbArrayToHex(fs_rule.params.stroke);
                }
                if (fs_rule.params["stroke-width"]) {
                    symbolizers.outlineWidth = fs_rule.params["stroke-width"];
                }
                if (fs_rule.params["stroke-dash-width"] && fs_rule.params["stroke-gap-width"]) {
                    symbolizers.outlineDasharray = [
                        fs_rule.params["stroke-dash-width"],
                        fs_rule.params["stroke-gap-width"],
                    ];
                }
                return new Promise((resolve, reject) => {
                    if (symbolizers) {
                        resolve(symbolizers);
                    }
                    else {
                        reject();
                    }
                });
            case "line":
                symbolizers = { kind: "Line" };
                if (fs_rule.params.stroke) {
                    symbolizers.color = this._rgbArrayToHex(fs_rule.params.stroke);
                    symbolizers.opacity = fs_rule.params.stroke[3];
                }
                if (fs_rule.params["stroke-width"]) {
                    if (fs_rule.params["stroke-width"] > 4) {
                        symbolizers.width = 4;
                    }
                    else {
                        symbolizers.width = fs_rule.params["stroke-width"];
                    }
                }
                if (fs_rule.params["stroke-dash-width"] && fs_rule.params["stroke-gap-width"]) {
                    symbolizers.dasharray = [fs_rule.params["stroke-dash-width"], fs_rule.params["stroke-gap-width"]];
                }
                return new Promise((resolve, reject) => {
                    if (symbolizers) {
                        resolve(symbolizers);
                    }
                    else {
                        reject();
                    }
                });
            case "point":
                if (fs_rule.params["font-marker"]) {
                    let fontmarkerdefinition;
                    if (fs_rule.params["font-marker"]["name"]) {
                        fontmarkerdefinition = StyleUtils.ParseFontMarkerParams(fs_rule.params["font-marker"], 12);
                    }
                    else {
                        fontmarkerdefinition = fs_rule.params["font-marker"];
                    }
                    const symbolizers = { kind: "Icon" };
                    return new Promise((resolve, reject) => {
                        StyleUtils.MakeTextTexture(fontmarkerdefinition, (texture) => {
                            symbolizers.image = this._texture2icondataurl(fs_rule, texture.image);
                            resolve(symbolizers);
                        }, (texture) => {
                            symbolizers.image = this._texture2icondataurl(fs_rule, texture.image);
                            reject();
                        });
                    })
                        .then((symbolizers) => {
                        if ("size" in fs_rule.params && fs_rule.params.size) {
                            symbolizers.size = fs_rule.params.size;
                        }
                        return new Promise((resolve, reject) => {
                            if (symbolizers) {
                                resolve(symbolizers);
                            }
                            else {
                                reject();
                            }
                        });
                    })
                        .catch((error) => console.log(error));
                }
                else if ("billboard" in fs_rule.params && fs_rule.params.billboard) {
                    symbolizers = { kind: "Icon" };
                    symbolizers.image = this._texture2icondataurl(fs_rule);
                    return new Promise((resolve, reject) => {
                        if (symbolizers) {
                            resolve(symbolizers);
                        }
                        else {
                            reject();
                        }
                    });
                }
                else {
                    symbolizers = { kind: "Mark", wellKnownName: "circle" };
                    if ("size" in fs_rule.params && fs_rule.params.size) {
                        symbolizers.radius = fs_rule.params.size;
                    }
                    if (fs_rule.params.stroke) {
                        symbolizers.color = this._rgbArrayToHex(fs_rule.params.stroke);
                        symbolizers.fillOpacity = fs_rule.params.stroke[3];
                    }
                    return new Promise((resolve, reject) => {
                        if (symbolizers) {
                            resolve(symbolizers);
                        }
                        else {
                            reject();
                        }
                    });
                }
        }
    }
    static _fsoutline2gsoutline(symbolizer) {
        symbolizer.outlineColor = symbolizer.color;
        symbolizer.outlineOpacity = symbolizer.opacity;
        if (symbolizer.dasharray) {
            symbolizer.outlineDasharray = symbolizer.dasharray;
        }
        if (symbolizer.width) {
            symbolizer.outlineWidth = symbolizer.width;
        }
        return symbolizer;
    }
    _texture2icondataurl(fs_rule, canvas_old) {
        if (canvas_old) {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (canvas_old.width > 0 && canvas_old.height > 0) {
                canvas.setAttribute("width", canvas_old.width.toString());
                canvas.setAttribute("height", canvas_old.height.toString());
                ctx.clearRect(-canvas_old.width / 2, -canvas_old.height / 2, canvas_old.width, canvas_old.height);
                ctx.drawImage(canvas_old, canvas_old.width / 4, canvas_old.height / 4, canvas_old.width, canvas_old.height, 0, 0, canvas_old.width * 2, canvas_old.height * 2);
                ctx.globalCompositeOperation = "source-in";
                if (fs_rule.params.stroke) {
                    ctx.fillStyle = this._rgbArrayToHex(fs_rule.params.stroke);
                }
                ctx.fillRect(-canvas.width / 2, -canvas.height / 2, canvas.width * 2, canvas.height * 2);
            }
            else {
                canvas.setAttribute("width", fs_rule.params.size.toString());
                canvas.setAttribute("height", fs_rule.params.size.toString());
            }
            return canvas.toDataURL("image/png");
        }
        else if ("billboard" in fs_rule.params) {
            return fs_rule.params.billboard;
        }
    }
}
//# sourceMappingURL=featurestyle.js.map